import React from "react";
import ProductsActionCreators from "@/Actions/Products";
import {
	BundleProduct,
	Product,
	ProductTemplate,
	ProductTemplates
} from "@/Contracts/Products";
import ZMessage from "@molecules/ZMessage";
import useDispatch from "@/Components/Utils/useDispatch";
import { useCallback, useState } from "react";
import { updateProductTemplates } from "@/Services/Products";
import ZTypography from "@atoms/ZTypography";
import ZButton from "@atoms/ZButton";
import ZModal from "@atoms/ZModal";
import ZEditOrCloneProductTemplateUpdateTable from "@templates/ZProductDetails/ZEditOrCloneProductTemplateUpdateTable";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";

const { ZText } = ZTypography;

const useUpdateTemplateModal = (
	currentProduct: Product | BundleProduct | null,
	drawerState: EProductDrawerState,
	onClose?: () => void
): Record<string, any> => {
	const dispatch = useDispatch();
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [checkedList, setCheckedList] = useState<string[]>([]);
	const [editedProduct, setEditedProduct] = useState<
		Product | BundleProduct | null
	>(null);
	const [templatesToUpdate, setTemplatesToUpdate] = useState<
		ProductTemplate[]
	>([]);

	const showModal = (templates: ProductTemplate[]) => {
		setTemplatesToUpdate([...templates]);

		setIsModalVisible(true);
	};

	const handleOk = async () => {
		if (currentProduct) {
			const productTemplates: ProductTemplates = {
				template_ids: [...checkedList].map(t => t.toString()),
				item_id:
					drawerState !== EProductDrawerState.BUNDLECHILDEDIT
						? (currentProduct as Product).id
						: (currentProduct as BundleProduct).item_id
			};

			try {
				await updateProductTemplates(productTemplates);
				dispatch(
					ProductsActionCreators.updateProductDetails(editedProduct)
				);
				onClose && onClose();

				ZMessage.success("You have successfully updated Product");
			} catch (error) {
				console.error(error);
			} finally {
				setIsModalVisible(false);
			}
		}
	};

	const handleCancel = () => {
		setTemplatesToUpdate([]);
		setCheckedList([]);
		setIsModalVisible(false);
	};

	const handleSelectedTemplates = useCallback(
		(templatesSelected: { templateName: string; templateId: string }[]) => {
			setCheckedList(
				[...templatesSelected].map(template => template.templateId)
			);
		},
		[templatesToUpdate]
	);

	const TemplateModal = (
		<ZModal
			modalBodyClassName="px-6 pt-4 pb-0"
			size={`md`}
			className={`template-confirm-modal`}
			title="Template updates"
			visible={isModalVisible}
			onOk={handleOk}
			onCancel={handleCancel}
			footer={
				<div className="flex align-center justify-between px-2 py-1.5">
					<ZText colorVariant={7} className="flex items-center">
						{checkedList.length + " Templates Selected"}
					</ZText>
					<div className="flex items-center justify-end">
						<ZButton key="back" onClick={handleCancel}>
							Cancel
						</ZButton>
						<ZButton key="submit" type="primary" onClick={handleOk}>
							Update
						</ZButton>
					</div>
				</div>
			}
		>
			<div className="mb-4">
				<ZText>The selected templates will be updated</ZText>
			</div>

			<ZEditOrCloneProductTemplateUpdateTable
				onDataSelection={handleSelectedTemplates}
				templatesToUpdate={templatesToUpdate}
			/>
		</ZModal>
	);

	return {
		setEditedProduct,
		showModal,
		TemplateModal
	};
};

export default useUpdateTemplateModal;
